import React, { useState, useRef, useEffect } from 'react';
import { Bot, X, Send, User, MessageSquare, Loader2, MessageCircleCode, Mic, MicOff } from 'lucide-react';
import axiosInstance from '../../utils/axios.config';
import ReactMarkdown from 'react-markdown';

const AIAssistant = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: 'assistant', text: 'مرحباً بك! أنا مساعدك الذكي. كيف يمكنني مساعدتك في القضايا أو المواعيد اليوم؟' }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const messagesEndRef = useRef(null);
    const recognitionRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // تهيئة محرك التعرف على الصوت
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = false;
            recognitionRef.current.lang = 'ar-SA';
            recognitionRef.current.interimResults = false;

            recognitionRef.current.onresult = (event) => {
                const transcript = event.results[0][0].transcript;
                setInput(transcript);
                setIsListening(false);
            };

            recognitionRef.current.onerror = (event) => {
                console.error("Speech Recognition Error:", event.error);
                setIsListening(false);
            };

            recognitionRef.current.onend = () => {
                setIsListening(false);
            };
        }
    }, []);

    const toggleListening = () => {
        if (!recognitionRef.current) {
            alert("عذراً، متصفحك لا يدعم خاصية التعرف على الصوت.");
            return;
        }

        if (isListening) {
            recognitionRef.current.stop();
        } else {
            setIsListening(true);
            recognitionRef.current.start();
        }
    };

    const handleSend = async () => {
        if (!input.trim()) return;

        const userMessage = { role: 'user', text: input };
        setMessages(prev => [...prev, userMessage]);
        const currentInput = input;
        setInput('');
        setIsLoading(true);

        try {
            const response = await axiosInstance.post('/ai/ask', { question: currentInput });
            const aiMessage = { role: 'assistant', text: response.data.answer };
            setMessages(prev => [...prev, aiMessage]);
        } catch (error) {
            setMessages(prev => [...prev, { role: 'assistant', text: 'عذراً، حدث خطأ أثناء الاتصال بالذكاء الاصطناعي.' }]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed bottom-6 left-6 z-[9999] font-sidebar">
            {/* Floating Button */}
            {!isOpen && (
                <button
                    onClick={() => setIsOpen(true)}
                    className="w-14 h-14 bg-primary text-white rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-transform active:scale-95 animate-bounce-slow"
                >
                    <Bot size={28} />
                </button>
            )}

            {/* Chat Window */}
            {isOpen && (
                <div className="w-[380px] h-[550px] bg-white rounded-2xl shadow-2xl border flex flex-col overflow-hidden animate-in slide-in-from-bottom-5">
                    {/* Header */}
                    <div className="bg-primary p-4 text-white flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white/20 rounded-lg">
                                <Bot size={22} />
                            </div>
                            <div>
                                <h3 className="font-bold text-sm">مساعد المحاماة الذكي</h3>
                                <p className="text-[10px] text-white/70">متصل الآن - مدعوم بـ Gemini</p>
                            </div>
                        </div>
                        <button onClick={() => setIsOpen(false)} className="hover:bg-white/10 p-1 rounded-full transition-colors">
                            <X size={20} />
                        </button>
                    </div>

                    {/* Messages Area */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50">
                        {messages.map((msg, idx) => (
                            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${
                                    msg.role === 'user' 
                                    ? 'bg-primary text-white rounded-br-none' 
                                    : 'bg-white border text-gray-800 shadow-sm rounded-bl-none'
                                }`}>
                                    <div className="prose prose-sm max-w-none text-right" dir="rtl">
                                        <ReactMarkdown>
                                            {msg.text}
                                        </ReactMarkdown>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {isLoading && (
                            <div className="flex justify-start">
                                <div className="bg-white border p-3 rounded-2xl rounded-bl-none shadow-sm flex items-center gap-2">
                                    <Loader2 className="animate-spin text-primary" size={16} />
                                    <span className="text-xs text-gray-500">جاري التفكير...</span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div className="p-4 bg-white border-t">
                        <div className="relative flex items-center gap-2">
                            <div className="relative flex-1">
                                <input
                                    type="text"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                                    placeholder={isListening ? "جاري الاستماع..." : "اسألني عن القضايا أو الجلسات..."}
                                    className={`w-full pr-4 pl-12 py-3 bg-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all text-right ${
                                        isListening ? 'ring-2 ring-red-400 animate-pulse bg-red-50' : 'focus:ring-primary/20'
                                    }`}
                                    dir="rtl"
                                />
                                <button 
                                    onClick={handleSend}
                                    className="absolute left-2 top-1.5 p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                                >
                                    <Send size={20} />
                                </button>
                            </div>
                            
                            <button
                                onClick={toggleListening}
                                className={`p-3 rounded-xl transition-all ${
                                    isListening 
                                    ? 'bg-red-500 text-white animate-pulse shadow-lg' 
                                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                }`}
                                title={isListening ? "إيقاف الاستماع" : "تحدث الآن"}
                            >
                                {isListening ? <Mic size={20} /> : <MicOff size={20} />}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AIAssistant;
